import { Maybe } from "@figurepos/fgr-lib-business-logic/lib/common/types"
import { max, min } from "lodash"
import { SEND_CSV_FILE_FAILURE, SEND_CSV_FILE_REQUESTED, SEND_CSV_FILE_SUCCESS } from "../../../api/actions"
import {
    CategoryAddRequestedPayload,
    CategoryUpdateRequestedPayload,
    ProductAddRequestedPayload,
    ProductUpdateRequestedPayload,
} from "../../../events/Category/types"
import type {
    CategoriesListItem,
    CategoriesListItemOverride,
    CategoriesListItemOverrideData,
    CategoriesListResponse,
    CategoryDetailResponse,
    CategoryModifierGroupResponse,
    CategoryModifierResponse,
    CategoryOptionGroupResponse,
} from "../../../graphql/types"
import { CategoryType } from "../../../graphql/types"
import { addItemToTree, updateItemInTree } from "../../../utils/tree"
import { ACCOUNT_SELECTED, REFETCH_DATA } from "../../auth/actions"
import { ASSIGN_MODIFIER_GROUP_TO_PRODUCT, ASSIGN_OPTION_GROUP_TO_PRODUCT } from "../../data/actions"
import {
    isMenuDataEntityInvalidated,
    ITEMS_AND_CATEGORIES_LIST_DATA_LOADED,
    ITEMS_AND_CATEGORIES_LIST_OVERRIDE_DATA_LOADED,
    ITEMS_CATEGORIES_AGENDA__MODIFIER_GROUPS_LOADED,
    ITEMS_CATEGORIES_AGENDA__MODIFIERS_LOADED,
    ITEMS_CATEGORIES_AGENDA__OPTION_GROUPS_LOADED,
    MENU_DATA_INVALIDATED,
} from "./actions"
import {
    CATEGORY_CREATED,
    CATEGORY_DELETED,
    CATEGORY_SEARCH_CLICKED,
    CATEGORY_UPDATED,
    FETCH_CATEGORY_DETAIL_SUCCESS,
    FETCH_UNMATCHED_ORDER_ITEM_NAMES_REQUESTED,
    FETCH_UNMATCHED_ORDER_ITEM_NAMES_SUCCESS,
    PRODUCT_CREATED,
    PRODUCT_DELETED,
    PRODUCT_DUPLICATE_SUCCESS,
    PRODUCT_UPDATED,
    PRODUCTS_AND_CATEGORIES_DELETED,
} from "./itemsCategories/actions"
import type { UnmatchedOrderItemNamesResponse } from "./itemsCategories/detail/request"
import { handleCategoriesListItemOverridesInsert } from "./itemsCategories/table/utils"
import {
    DELETE_MODIFIER_GROUP_CLICKED,
    MODIFIER_GROUP_DATA_UPDATED,
    MODIFIER_GROUP_LOCATION_SAVE_SUCCESS,
    MODIFIER_GROUP_SAVE_SUCCESS,
    MODIFIER_GROUPS_DELETED,
} from "./modifierGroups/actions"
import {
    DELETE_MODIFIER_CLICKED,
    MODIFIER_LOCATION_SAVE_SUCCESS,
    MODIFIER_SAVE_SUCCESS,
    MODIFIERS_DELETED,
} from "./modifiers/actions"
import {
    DELETE_OPTION_GROUP_CLICKED,
    OPTION_GROUP_LOCATION_SAVE_SUCCESS,
    OPTION_GROUP_SAVE_SUCCESS,
    OPTION_GROUPS_DELETED,
} from "./optionGroups/actions"
import {
    getIsActiveLocationsOverrides,
    getProductListIsActive,
    getProductListItemPrice,
    getProductListItemPriceForItemAdd,
    getProductPricingType,
    getProductPricingTypeForProductAdd,
    getTileImagePathFromImages,
} from "./utils"

export type CsvStatusType = {
    processing: boolean
    success: boolean
    message: Maybe<string>
}

export type ItemsCategoriesState = {
    optionGroups: Maybe<CategoryOptionGroupResponse>
    modifierGroups: Maybe<CategoryModifierGroupResponse>
    modifiers: Maybe<CategoryModifierResponse>
    itemsAndCategoriesList: Maybe<CategoriesListResponse>
    itemsAndCategoriesListLocationId: Maybe<string>
    newlyCreatedOptionGroupId: Maybe<string>
    newlyCreatedModifierGroupId: Maybe<string>
    csvImportedStatus: CsvStatusType
    categoryDetails: CategoryDetailResponse[]
    searchValue: Maybe<string>
    unmatchedOrderItemNames: Maybe<UnmatchedOrderItemNamesResponse>
}

export const createDefaultItemsCategoriesState = () => ({
    optionGroups: null,
    modifiers: null,
    modifierGroups: null,
    itemsAndCategoriesList: null,
    itemsAndCategoriesListLocationId: null,
    newlyCreatedOptionGroupId: null,
    newlyCreatedModifierGroupId: null,
    csvImportedStatus: {
        processing: false,
        success: false,
        message: null,
    },
    fileToUpload: null,
    categoryDetails: [],
    searchValue: null,
    unmatchedOrderItemNames: null,
})

export const itemsCategoriesReducer = (state: Maybe<ItemsCategoriesState> = undefined, action: Record<string, any>) => {
    if (!state) {
        return createDefaultItemsCategoriesState()
    }

    switch (action.type) {
        case ACCOUNT_SELECTED:
        case REFETCH_DATA:
            return createDefaultItemsCategoriesState()

        case MENU_DATA_INVALIDATED:
            return {
                ...state,
                itemsAndCategoriesList: isMenuDataEntityInvalidated(action, "itemsList")
                    ? null
                    : state.itemsAndCategoriesList,
                // consumers refetch by comparing their location against this marker, so it must go too
                itemsAndCategoriesListLocationId: isMenuDataEntityInvalidated(action, "itemsList")
                    ? null
                    : state.itemsAndCategoriesListLocationId,
                categoryDetails: isMenuDataEntityInvalidated(action, "itemDetails") ? [] : state.categoryDetails,
                optionGroups: isMenuDataEntityInvalidated(action, "optionGroups") ? null : state.optionGroups,
                modifierGroups: isMenuDataEntityInvalidated(action, "modifierGroups") ? null : state.modifierGroups,
                modifiers: isMenuDataEntityInvalidated(action, "modifiers") ? null : state.modifiers,
            }

        case ASSIGN_OPTION_GROUP_TO_PRODUCT:
            return { ...state, newlyCreatedOptionGroupId: action.payload.optionGroupId }

        case ASSIGN_MODIFIER_GROUP_TO_PRODUCT:
            return { ...state, newlyCreatedModifierGroupId: action.payload.modifierGroupId }

        case SEND_CSV_FILE_REQUESTED:
            return {
                ...state,
                csvImportedStatus: {
                    processing: true,
                    success: false,
                    message: null,
                },
            }

        case SEND_CSV_FILE_SUCCESS:
            return {
                ...state,
                csvImportedStatus: {
                    ...state.csvImportedStatus,
                    processing: false,
                    success: true,
                    message: action.payload.message,
                },
            }

        case SEND_CSV_FILE_FAILURE:
            return {
                ...state,
                csvImportedStatus: {
                    ...state.csvImportedStatus,
                    processing: false,
                    success: false,
                    message: action.payload.message ?? null,
                },
            }

        case ITEMS_CATEGORIES_AGENDA__OPTION_GROUPS_LOADED:
            return { ...state, optionGroups: action.payload.data }

        case ITEMS_CATEGORIES_AGENDA__MODIFIERS_LOADED:
            return { ...state, modifiers: action.payload.data }

        case ITEMS_CATEGORIES_AGENDA__MODIFIER_GROUPS_LOADED:
            return { ...state, modifierGroups: action.payload.data }

        case ITEMS_AND_CATEGORIES_LIST_DATA_LOADED:
            return {
                ...state,
                categoryDetails: [],
                itemsAndCategoriesList: {
                    data: action.payload.data.data,
                    overrides: handleCategoriesListItemOverridesInsert(
                        state.itemsAndCategoriesList?.overrides ?? [],
                        action.payload.data.overrides,
                        null
                    ),
                },
                itemsAndCategoriesListLocationId: action.payload.locationId ?? null,
            }

        case ITEMS_AND_CATEGORIES_LIST_OVERRIDE_DATA_LOADED: {
            return {
                ...state,
                itemsAndCategoriesList: {
                    data: state.itemsAndCategoriesList?.data ?? [],
                    overrides: handleCategoriesListItemOverridesInsert(
                        state.itemsAndCategoriesList?.overrides ?? [],
                        action.payload.overrides ?? [],
                        action.payload.locationId
                    ),
                },
            }
        }

        case FETCH_CATEGORY_DETAIL_SUCCESS:
            return { ...state, categoryDetails: [action.payload.data] }

        case CATEGORY_DELETED:
        case PRODUCT_DELETED: {
            if (!state.itemsAndCategoriesList || state.itemsAndCategoriesList.data.length === 0) {
                return { ...state }
            }

            return {
                ...state,
                itemsAndCategoriesList: {
                    ...state.itemsAndCategoriesList,
                    data: state.itemsAndCategoriesList.data
                        .filter(item => item.id !== action.payload.categoryId)
                        .map(item =>
                            item.parentId === action.payload.categoryId
                                ? { ...item, parentId: action.payload.parentId }
                                : item
                        ),
                },
            }
        }

        case PRODUCTS_AND_CATEGORIES_DELETED: {
            if (!state.itemsAndCategoriesList || state.itemsAndCategoriesList.data.length === 0) {
                return { ...state }
            }

            const data = state.itemsAndCategoriesList.data
            const productIds = action.payload.productIds

            return {
                ...state,
                itemsAndCategoriesList: {
                    ...state.itemsAndCategoriesList,
                    data: data
                        .filter(item => !productIds.includes(item.id))
                        .map(item => {
                            const deletedParentCategory = data.find(
                                c => productIds.includes(c.id) && item.parentId === c.id
                            )
                            return deletedParentCategory ? { ...item, parentId: deletedParentCategory.parentId } : item
                        }),
                },
            }
        }

        case CATEGORY_CREATED: {
            const itemsState = state.itemsAndCategoriesList

            if (!itemsState?.data?.length) {
                return { ...state }
            }

            const categoryToSave: CategoryAddRequestedPayload = action.payload.data
            const categoryListItem: CategoriesListItem = {
                id: categoryToSave.categoryId,
                name: categoryToSave.name,
                displayName: categoryToSave.shortName,
                parentId: categoryToSave.parentCategoryId,
                type: CategoryType.Category,
                isActive: categoryToSave.isActive,
                icon: categoryToSave.icon,
                color: categoryToSave.color,
                price: null,
                unit: null,
                tileImagePath: null,
                displayImagePOS: false,
                children: null,
            }

            const addedToTree = addItemToTree(itemsState.data, categoryToSave.parentCategoryId, categoryListItem)

            // The items & categories in redux are stored both as tree data and flat list :facepalm:
            // For that reason we need to update it both ways here - update in tree, and appeding to flat list
            // We will get rid of all of this once we refactor fetching the Items & Categories list etc.
            // todo: do this for updating / removing as well (for categories also)
            const updatedData: CategoriesListItem[] = [...addedToTree, categoryListItem]

            return {
                ...state,
                itemsAndCategoriesList: {
                    ...state.itemsAndCategoriesList,
                    data: updatedData,
                    overrides: getIsActiveLocationsOverrides(state.itemsAndCategoriesList, categoryToSave, true),
                },
            }
        }

        case CATEGORY_UPDATED: {
            const itemsState = state.itemsAndCategoriesList
            if (!itemsState?.data?.length) {
                return { ...state }
            }

            const categoryToSave: CategoryUpdateRequestedPayload = action.payload.data
            const isInMasterList = !categoryToSave.locationId

            if (isInMasterList) {
                // Category Updated in masterlist
                const categoryListItem: CategoriesListItem = {
                    id: categoryToSave.categoryId,
                    name: categoryToSave.name as string,
                    displayName: categoryToSave.shortName,
                    parentId: categoryToSave.parentCategoryId,
                    type: CategoryType.Category,
                    isActive: categoryToSave.isActive?.value,
                    icon: categoryToSave.icon,
                    color: categoryToSave.color,
                    price: null,
                    unit: null,
                    tileImagePath: null,
                    displayImagePOS: false,
                    children: null,
                }

                return {
                    ...state,
                    itemsAndCategoriesList: {
                        ...state.itemsAndCategoriesList,
                        data: itemsState.data.map(c =>
                            c.id !== categoryToSave.categoryId ? c : { ...categoryListItem, children: c.children }
                        ),
                        overrides: getIsActiveLocationsOverrides(state.itemsAndCategoriesList, categoryToSave, false),
                    },
                }
            } else {
                // Category Updated in specific location (override)
                const overrideToSave = categoryToSave
                const findLocationOverride = itemsState.overrides.find(
                    o => o.locationId === categoryToSave.locationId && o.data.id === categoryToSave.categoryId
                )
                const categoryListItemOverrideData: CategoriesListItemOverrideData = {
                    id: overrideToSave.categoryId,
                    isActive: overrideToSave.isActive?.value,
                    price: undefined,
                }
                const categoryListItemOverride: CategoriesListItemOverride = {
                    locationId: categoryToSave.locationId as string,
                    data: categoryListItemOverrideData,
                }

                // New Override
                if (!findLocationOverride) {
                    return {
                        ...state,
                        itemsAndCategoriesList: {
                            ...itemsState,
                            overrides: [...itemsState.overrides, categoryListItemOverride],
                        },
                    }
                } // Updated Override
                else {
                    return {
                        ...state,
                        itemsAndCategoriesList: {
                            ...state.itemsAndCategoriesList,
                            overrides: itemsState.overrides.map(o =>
                                o.data.id === overrideToSave.categoryId && o.locationId === categoryToSave.locationId
                                    ? categoryListItemOverride
                                    : o
                            ),
                        },
                    }
                }
            }
        }

        case PRODUCT_CREATED: {
            const itemsState = state.itemsAndCategoriesList
            if (!itemsState?.data?.length) {
                return { ...state }
            }

            const productToSave: ProductAddRequestedPayload = action.payload.data
            const find = itemsState.data.find(d => d.id === productToSave.productId)

            const isOverride = !!productToSave.locationId
            const productIsActive = getProductListIsActive(isOverride, productToSave, find)

            const categoryListItem: CategoriesListItem = {
                id: productToSave.productId,
                name: productToSave.name,
                displayName: productToSave.displayName,
                parentId: productToSave.parentCategoryId,
                type: CategoryType.Product,
                isActive: productIsActive,
                icon: productToSave.icon,
                color: productToSave.color,
                price: getProductListItemPrice(productToSave, find),
                pricingType: getProductPricingType(productToSave, find),
                unit: productToSave.unit,
                tileImagePath: getTileImagePathFromImages(productToSave.images),
                displayImagePOS: productToSave.displayImagePOS ?? false,
                children: null,
            }

            const addedToTree = addItemToTree(itemsState.data, productToSave.parentCategoryId, categoryListItem)

            // The items & categories in redux are stored both as tree data and flat list :facepalm:
            // For that reason we need to update it both ways here - update in tree, and appeding to flat list
            // We will get rid of all of this once we refactor fetching the Items & Categories list etc.
            // todo: do this for updating / removing as well (for categories also)
            const updatedData: CategoriesListItem[] = [...addedToTree, categoryListItem]

            // New Product created for specific location
            if (productToSave.locationId) {
                return {
                    ...state,
                    itemsAndCategoriesList: {
                        ...state.itemsAndCategoriesList,
                        data: updatedData,
                        overrides: isOverride
                            ? [
                                  ...(itemsState.overrides ?? []),
                                  {
                                      locationId: productToSave.locationId,
                                      data: {
                                          id: productToSave.productId,
                                          isActive: productToSave.isActive,
                                          price: getProductListItemPrice(productToSave, null),
                                      },
                                  },
                              ]
                            : itemsState.overrides,
                    },
                }
            }
            // New product created in master list
            return {
                ...state,
                itemsAndCategoriesList: {
                    ...state.itemsAndCategoriesList,
                    data: updatedData,
                    overrides: getIsActiveLocationsOverrides(state.itemsAndCategoriesList, productToSave, true),
                },
            }
        }

        case PRODUCT_UPDATED: {
            const itemsState = state.itemsAndCategoriesList
            if (!itemsState?.data?.length) {
                return { ...state }
            }

            const productToSave: ProductUpdateRequestedPayload = action.payload.data
            const isInMasterList = !productToSave.locationId

            if (isInMasterList) {
                // Product Updated in masterlist

                const find = itemsState.data.find(d => d.id === productToSave.productId)
                const isOverride = !!productToSave.locationId
                const productIsActive = getProductListIsActive(isOverride, productToSave, find)

                const categoryListItem: CategoriesListItem = {
                    id: productToSave.productId,
                    name: productToSave.name,
                    displayName: productToSave.displayName,
                    parentId: productToSave.parentId,
                    type: CategoryType.Product,
                    isActive: productIsActive,
                    icon: productToSave.icon,
                    color: productToSave.color,
                    price: getProductListItemPrice(productToSave, find),
                    pricingType: getProductPricingType(productToSave, find),
                    unit: productToSave.unit,
                    tileImagePath: getTileImagePathFromImages(productToSave.images),
                    displayImagePOS: productToSave.displayImagePOS ?? false,
                    children: null,
                }

                const updatedData = updateItemInTree(itemsState.data, productToSave.productId, data => ({
                    ...data,
                    ...categoryListItem,
                    children: data.children,
                }))

                return {
                    ...state,
                    itemsAndCategoriesList: {
                        ...state.itemsAndCategoriesList,
                        data: updatedData,
                        overrides: getIsActiveLocationsOverrides(state.itemsAndCategoriesList, productToSave, false),
                    },
                }
            } else {
                // Product Updated in specific location (override)
                const overrideToSave = productToSave
                const findLocationOverride = itemsState.overrides.find(
                    o => o.locationId === productToSave.locationId && o.data.id === productToSave.productId
                )
                const categoryListItemOverrideData: CategoriesListItemOverrideData = {
                    id: overrideToSave.productId,
                    isActive: overrideToSave.isActive,
                    price: getProductPricePerLocation(overrideToSave),
                }
                const categoryListItemOverride: CategoriesListItemOverride = {
                    locationId: productToSave.locationId as string,
                    data: categoryListItemOverrideData,
                }

                // New Override
                if (!findLocationOverride) {
                    return {
                        ...state,
                        itemsAndCategoriesList: {
                            ...itemsState,
                            overrides: [...itemsState.overrides, categoryListItemOverride],
                        },
                    }
                } // Updated Override
                else {
                    return {
                        ...state,
                        itemsAndCategoriesList: {
                            ...state.itemsAndCategoriesList,
                            overrides: itemsState.overrides.map(o =>
                                o.data.id === overrideToSave.productId && o.locationId === productToSave.locationId
                                    ? categoryListItemOverride
                                    : o
                            ),
                        },
                    }
                }
            }
        }

        case PRODUCT_DUPLICATE_SUCCESS: {
            const itemsState = state.itemsAndCategoriesList
            if (!itemsState || !itemsState.data || itemsState.data.length === 0) {
                return { ...state }
            }

            const productToAdd: ProductAddRequestedPayload = action.payload.data
            const categoryListItem: CategoriesListItem = {
                id: productToAdd.productId,
                name: productToAdd.name,
                parentId: productToAdd.parentCategoryId,
                type: CategoryType.Product,
                isActive: productToAdd.isActive,
                icon: productToAdd.icon,
                color: productToAdd.color,
                price: getProductListItemPriceForItemAdd(productToAdd),
                pricingType: getProductPricingTypeForProductAdd(productToAdd),
                unit: productToAdd.unit,
                tileImagePath: null,
                displayImagePOS: false,
                children: null,
            }

            const addedOverRides: CategoriesListItemOverride[] = (productToAdd?.isActiveLocations ?? []).map(o => {
                return {
                    locationId: o.id,
                    data: {
                        id: productToAdd.productId,
                        isActive: o.isActive,
                    },
                }
            })

            return {
                ...state,
                itemsAndCategoriesList: {
                    ...state.itemsAndCategoriesList,
                    data: [...itemsState.data, categoryListItem],
                    overrides: [...itemsState.overrides, ...addedOverRides],
                },
            }
        }

        case OPTION_GROUP_SAVE_SUCCESS: {
            const optionGroupsState = state.optionGroups

            // Update
            if (optionGroupsState?.data.find(og => og.id === action.payload.data.id)) {
                return {
                    ...state,
                    optionGroups: {
                        ...optionGroupsState,
                        data: optionGroupsState?.data.map(c =>
                            c.id !== action.payload.data.id ? c : action.payload.data
                        ),
                    },
                }
            } else {
                return {
                    ...state,
                    optionGroups: {
                        ...optionGroupsState,
                        data: [...(optionGroupsState?.data ?? []), action.payload.data],
                    },
                }
            }
        }

        case OPTION_GROUP_LOCATION_SAVE_SUCCESS: {
            const optionGroupsState = state.optionGroups

            if (!optionGroupsState || !optionGroupsState.overrides) {
                return { ...state }
            }

            // If Override was canceled, delete override from store
            const saveRequestData = action.payload.saveRequestData

            if (
                !action.payload.data &&
                optionGroupsState.overrides.find(
                    og => og.locationId === action.payload.locationId && og.data.id === saveRequestData.id
                )
            ) {
                return {
                    ...state,
                    optionGroups: {
                        ...optionGroupsState,
                        overrides: optionGroupsState.overrides.filter(c => c.data.id !== saveRequestData.id),
                    },
                }
            }

            const findOverride = optionGroupsState.overrides.find(
                og => og.locationId === action.payload.locationId && og.data.id === action.payload.data.data.id
            )

            // Update
            if (findOverride) {
                return {
                    ...state,
                    optionGroups: {
                        ...optionGroupsState,
                        overrides: optionGroupsState.overrides.map(c =>
                            c.locationId === action.payload.locationId && c.data.id === action.payload.data.data.id
                                ? action.payload.data
                                : c
                        ),
                    },
                }
            } // Create
            else {
                return {
                    ...state,
                    optionGroups: {
                        ...optionGroupsState,
                        overrides: [...optionGroupsState.overrides, action.payload.data],
                    },
                }
            }
        }

        case DELETE_OPTION_GROUP_CLICKED: {
            const optionGroupsState = state.optionGroups
            if (!optionGroupsState || !optionGroupsState.data || optionGroupsState.data.length === 0) {
                return { ...state }
            }

            return {
                ...state,
                optionGroups: {
                    ...optionGroupsState,
                    data: optionGroupsState.data.filter(og => og.id !== action.payload.optionGroupId),
                },
            }
        }

        case OPTION_GROUPS_DELETED: {
            const optionGroupsState = state.optionGroups
            if (!optionGroupsState || !optionGroupsState.data || optionGroupsState.data.length === 0) {
                return { ...state }
            }

            const ids: string[] = action.payload.ids

            return {
                ...state,
                optionGroups: {
                    ...optionGroupsState,
                    data: optionGroupsState.data.filter(og => !ids.includes(og.id)),
                },
            }
        }

        // Modifier Groups
        case MODIFIER_GROUP_SAVE_SUCCESS: {
            const modifierGroupsState = state.modifierGroups

            // Update
            if (modifierGroupsState?.data.find(og => og.id === action.payload.data.id)) {
                return {
                    ...state,
                    modifierGroups: {
                        ...modifierGroupsState,
                        data: modifierGroupsState?.data?.map(c =>
                            c.id !== action.payload.data.id ? c : action.payload.data
                        ),
                    },
                }
            } else {
                return {
                    ...state,
                    modifierGroups: {
                        ...modifierGroupsState,
                        data: [...(modifierGroupsState?.data ?? []), action.payload.data],
                    },
                }
            }
        }

        case MODIFIER_GROUP_LOCATION_SAVE_SUCCESS: {
            const modifierGroupsState = state.modifierGroups

            if (!modifierGroupsState || !modifierGroupsState.overrides) {
                return { ...state }
            }

            // If Override was canceled, delete override from store
            const id = action.payload.id

            if (
                !action.payload.data &&
                modifierGroupsState.overrides.find(
                    og => og.locationId === action.payload.locationId && og.data.id === id
                )
            ) {
                return {
                    ...state,
                    modifierGroups: {
                        ...modifierGroupsState,
                        overrides: modifierGroupsState.overrides.filter(c => c.data.id !== id),
                    },
                }
            }

            const findOverride = modifierGroupsState.overrides.find(
                og => og.locationId === action.payload.locationId && og.data.id === action.payload.data.data.id
            )

            // Update
            if (findOverride) {
                return {
                    ...state,
                    modifierGroups: {
                        ...modifierGroupsState,
                        overrides: modifierGroupsState.overrides.map(c =>
                            c.locationId === action.payload.locationId && c.data.id === action.payload.data.data.id
                                ? action.payload.data
                                : c
                        ),
                    },
                }
            } // Create
            else {
                return {
                    ...state,
                    modifierGroups: {
                        ...modifierGroupsState,
                        overrides: [...modifierGroupsState.overrides, action.payload.data],
                    },
                }
            }
        }

        case DELETE_MODIFIER_GROUP_CLICKED: {
            const modifierGroupsState = state.modifierGroups
            if (!modifierGroupsState || !modifierGroupsState.data || modifierGroupsState.data.length === 0) {
                return { ...state }
            }

            return {
                ...state,
                modifierGroups: {
                    ...modifierGroupsState,
                    data: modifierGroupsState.data.filter(og => og.id !== action.payload.modifierGroupId),
                },
            }
        }

        case MODIFIER_GROUPS_DELETED: {
            const modifierGroupsState = state.modifierGroups
            if (!modifierGroupsState || !modifierGroupsState.data || modifierGroupsState.data.length === 0) {
                return { ...state }
            }

            const ids: string[] = action.payload.ids

            return {
                ...state,
                modifierGroups: {
                    ...modifierGroupsState,
                    data: modifierGroupsState.data.filter(og => !ids.includes(og.id)),
                },
            }
        }

        // Modifiers
        case MODIFIER_SAVE_SUCCESS: {
            const modifierState = state.modifiers

            // Update
            if (modifierState?.data?.find(og => og.id === action.payload.data.id)) {
                return {
                    ...state,
                    modifiers: {
                        ...modifierState,
                        data: modifierState?.data?.map(c =>
                            c.id !== action.payload.data.id ? c : action.payload.data
                        ),
                    },
                }
            } else {
                return {
                    ...state,
                    modifiers: { ...modifierState, data: [...(modifierState?.data ?? []), action.payload.data] },
                }
            }
        }

        case MODIFIER_LOCATION_SAVE_SUCCESS: {
            const modifiersState = state.modifiers
            const requestData = action.payload.requestData

            if (!modifiersState || !modifiersState.overrides) {
                return { ...state }
            }

            const override = {
                locationId: action.payload.locationId,
                data: {
                    id: requestData.id,
                    isActive: requestData.isActive,
                    addedToGroups: [],
                },
            }
            const findOverride = modifiersState.overrides.find(
                og => og.locationId === action.payload.locationId && og.data.id === override.data.id
            )

            if (findOverride) {
                return {
                    ...state,
                    modifiers: {
                        ...modifiersState,
                        overrides: modifiersState.overrides.map(c =>
                            c.locationId === action.payload.locationId && c.data.id === override.data.id ? override : c
                        ),
                    },
                }
            } else {
                return {
                    ...state,
                    modifiers: { ...modifiersState, overrides: [...modifiersState.overrides, override] },
                }
            }
        }

        case DELETE_MODIFIER_CLICKED: {
            const modifiersState = state.modifiers
            if (!modifiersState || !modifiersState.data || modifiersState.data.length === 0) {
                return { ...state }
            }

            return {
                ...state,
                modifiers: {
                    ...modifiersState,
                    data: modifiersState.data.filter(og => og.id !== action.payload.modifierId),
                },
            }
        }

        case MODIFIERS_DELETED: {
            const modifiersState = state.modifiers
            if (!modifiersState || !modifiersState.data || modifiersState.data.length === 0) {
                return { ...state }
            }

            const ids: string[] = action.payload.ids

            return {
                ...state,
                modifiers: {
                    ...modifiersState,
                    data: modifiersState.data.filter(og => !ids.includes(og.id)),
                },
            }
        }

        case MODIFIER_GROUP_DATA_UPDATED: {
            const modifiersState = state.modifiers
            if (!modifiersState || !modifiersState.data || modifiersState.data.length === 0) {
                return { ...state }
            }

            const newValues = action.payload.newValue.items || []
            return {
                ...state,
                modifiers: {
                    ...modifiersState,
                    data: modifiersState.data.map(modifier => {
                        const find = newValues.find(nw => nw.modifierId === modifier.id)
                        return find
                            ? {
                                  ...modifier,
                                  addedToGroups: modifier.addedToGroups.map(adtg =>
                                      adtg.groupId === action.payload.newValue.modifierGroupId
                                          ? { ...adtg, price: find.price * 100, supplement: find.groupExtra * 100 }
                                          : adtg
                                  ),
                              }
                            : modifier
                    }),
                },
            }
        }

        case CATEGORY_SEARCH_CLICKED: {
            return { ...state, searchValue: action.payload.searchValue }
        }

        case FETCH_UNMATCHED_ORDER_ITEM_NAMES_REQUESTED:
            return { ...state, unmatchedOrderItemNames: null }

        case FETCH_UNMATCHED_ORDER_ITEM_NAMES_SUCCESS:
            return { ...state, unmatchedOrderItemNames: action.payload.data }

        default:
            return state
    }
}

const getProductPricePerLocation = (productToSave: ProductUpdateRequestedPayload): Maybe<number[]> => {
    if (productToSave.optionGroup?.options?.length) {
        const optionsPrices = productToSave.optionGroup.options.map(val => val.price).filter(a => a)

        if (!optionsPrices || optionsPrices.length === 0) {
            return null
        }

        return [min(optionsPrices), max(optionsPrices)]
    }

    if (productToSave.fixedPrice != null) {
        return [productToSave.fixedPrice]
    }

    return []
}
